import { AsyncPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption
} from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatChipGrid,
  MatChipInput,
  MatChipRemove,
  MatChipRow
} from '@angular/material/chips';
import {
  MatDateRangeInput,
  MatDateRangePicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatEndDate,
  MatStartDate
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatHint, MatSuffix } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, debounceTime, map, mergeMap, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InlineSvgComponent } from '../../../../components/inline-svg/inline-svg.component';
import { FileFilterBody } from '../../../../models/general.models';
import { User } from '../../../../models/user.model';
import { UserService } from '../../../../services/user.service';

@Component({
  selector: 'frmg-file-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    ReactiveFormsModule,
    InlineSvgComponent,
    MatTooltip,
    MatCheckbox,
    MatFormField,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatHint,
    MatSuffix,
    MatDateRangePicker,
    MatDatepickerActions,
    MatButton,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    AsyncPipe
  ]
})
export class FileFilterComponent implements OnInit {
  public filterGroup: FormGroup;
  public ownerCtrl: FormControl = new FormControl('');
  public types: string[] = ['xlsx', 'kml', 'zip', 'csv'];
  public users$: Observable<Array<User>>;
  public addedOwners: Array<User> = [];

  @Input() filter: FileFilterBody;
  @Input() metadataKeys: Array<string>;

  @Output() closeMenu = new EventEmitter<any>();
  @Output() onApply = new EventEmitter<FileFilterBody>();

  @ViewChild('ownerInput') ownerInput: ElementRef<HTMLInputElement>;

  constructor(private fb: FormBuilder, private userService: UserService) {
    this.filterGroup = this.fb.group({
      file_format: this.filter?.file_format || [null],
      date: this.fb.group({
        from: this.filter?.date.from || [null],
        to: this.filter?.date.to || [null]
      }),
      ownership: this.filter?.ownership || []
    });
    this.addedOwners = this.filter?.ownership || [];
    this.users$ = this.ownerCtrl.valueChanges.pipe(
      debounceTime(500),
      mergeMap((query) =>
        query
          ? this.userService.searchTeamUsers(query).pipe(
            map((users) => {
              return users.filter(
                (user) =>
                  !this.addedOwners.find((el) => el.email === user.email)
              );
            }),
            catchError((_err) => of([]))
          )
          : of([])
      )
    );
  }

  ngOnInit(): void {}

  remove(user: User) {
    this.addedOwners.splice(
      this.addedOwners.findIndex((el) => el.email == user.email),
      1
    );
  }

  selected(event: MatAutocompleteSelectedEvent) {
    if (this.ownerInput) {
      this.addedOwners.push(event.option.value);
      this.ownerInput.nativeElement.value = '';
      this.ownerCtrl.setValue('');
    }
  }

  public isSelected(type: string) {
    return this.filterGroup.get('file_format')?.value?.includes(type);
  }

  public select(type: string) {
    const currentValue =
      this.filterGroup.get('file_format')?.value?.split(',') || [];
    if (this.isSelected(type)) {
      currentValue.splice(currentValue.indexOf(type), 1);
    } else {
      currentValue.push(type);
    }
    this.filterGroup.get('file_format').setValue(currentValue.join(','));
  }

  public clear() {
    this.addedOwners = [];
    this.filterGroup.setValue({
      file_format: null,
      date: {
        from: '',
        to: ''
      },
      ownership: []
    });
    this.onApply.emit({
      file_format: null,
      date: {
        from: null,
        to: null
      },
      ownership: []
    });
  }

  public clearDate() {
    this.filterGroup.get('date').setValue({
      from: null,
      to: null
    });
  }

  public apply() {
    const values = this.filterGroup.value;
    values.ownership = this.addedOwners;
    this.onApply.emit(values);
    this.closeMenu.emit();
  }
}
